import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Stack,
  CssBaseline,
  Modal,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  useTheme,
  Popper,
  Paper,
  Select,
} from "@mui/material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Topbar from "../../../../components/topbar/TopBar";
import Header from "../../../../components/header/Header";
import { getTokenRefresh } from "../../../../helpers/token/refreshToken";
import SnackBar from "../../../../components/snackbar/SnackBar";

import { DataGrid, esES } from "@mui/x-data-grid";

//sidebar
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import Sidebar from "../../../../components/sidebar/Sidebar";
import { tokens } from "../../../../theme";
import { modalStyle } from "../../../../views/users/admin/driver/DriverStyle";
import {
  deleteVehicleApi,
  getVehicleList,
  getVehicles,
  getVehiclesUpdated,
  updateVehicleTypeApi,
} from "../../../../api/users/driver/vehicle";
import { dateFormat } from "../../../../helpers/date/date";

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
  const { width, value } = props;
  const wrapper = React.useRef(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: 1,
        height: 1,
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: 1,
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            style={{ minHeight: wrapper.current.offsetHeight - 3 }}
          >
            <Typography variant="body2" style={{ padding: 8 }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

const VehicleRenovateList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [UID, setUID] = useState("");
  const [STATUS, setSTATUS] = useState("");
  const [dataSelected, setDataSelected] = useState([]);
  const [openTypeModal, setOpenTypeModal] = useState(false);
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  const [selectedTypeVehicleId, setSelectedTypeVehicleId] = useState("");
  const vehicleStatus = { 1: "Activo", 2: "Suspendido", 3: "Eliminado" };
  const vehiclePremium = { 0: "NO", 1: "SI" };

  const getVehicleTypeValue = (type) => {
    if (type === null || type === undefined || type === "") {
      return "";
    }

    if (Number(type) === 0) {
      return "none";
    }

    const vehicleType = vehicleTypes.find(
      (item) => String(item.id) === String(type),
    );

    return vehicleType?.name || String(type);
  };

  const getVehicleTypeSelectValue = (type) => {
    if (type === null || type === undefined || type === "") {
      return "0";
    }

    return String(type);
  };

  const handleTypeModalOpen = (vehicle) => {
    setSelectedTypeVehicleId(vehicle.id);
    setSelectedVehicleType(getVehicleTypeSelectValue(vehicle.type));
    setOpenTypeModal(true);
  };

  const handleTypeModalClose = () => {
    setOpenTypeModal(false);
    setSelectedVehicleType("");
    setSelectedTypeVehicleId("");
  };

  const updateVehicleType = async () => {
    const vehicleId = Number(selectedTypeVehicleId);
    const typeId = Number(selectedVehicleType);

    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      snackBarDialog("Vehiculo invalido", "error");
      return;
    }

    if (!Number.isInteger(typeId) || typeId < 0) {
      snackBarDialog("Tipo de vehiculo invalido", "error");
      return;
    }

    setIsLoading(true);
    try {
      await updateVehicleTypeApi(vehicleId, typeId);
      setVehicles((currentVehicles) =>
        currentVehicles.map((vehicle) =>
          vehicle.id === selectedTypeVehicleId
            ? { ...vehicle, type: typeId }
            : vehicle,
        ),
      );
      snackBarDialog("Tipo de vehiculo actualizado");
      handleTypeModalClose();
    } catch (error) {
      console.log(error);
      const message =
        error.response?.data?.message || "Error al actualizar tipo de vehiculo";
      snackBarDialog(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const listVehicles = async () => {
      try {
        try {
          const vehicleListResponse = await getVehicleList();
          console.log(vehicleListResponse);
          setVehicleTypes(vehicleListResponse.data?.data || []);
        } catch (error) {
          console.log(error);
          setVehicleTypes([]);
        }

        const token = getTokenRefresh();
        if (token) {
          const response = await getVehiclesUpdated();
          setVehicles([]);
          Object.values(response.data.data).map((vehicle, index) => {
            if (vehicle.status !== 3) {
              vehicle.vehicleStatus = vehicleStatus[vehicle.status];
              vehicle.vehiclePremium = vehiclePremium[vehicle.premium];
              setVehicles((oldData) => [...oldData, vehicle]);
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };
    listVehicles();
  }, []);

  const columns = [
    { field: "id", headerName: "ID", width: 40 },
    {
      headerName: "Propietario",
      field: "owner",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 120,
      renderCell: renderCellExpand,
    },
    {
      headerName: "Marca",
      field: "brand",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
    },
    {
      headerName: "Model",
      field: "model",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 70,
    },
    {
      headerName: "Carroceria",
      field: "bodywork",
      width: 80,
    },
    {
      headerName: "Tipo",
      field: "type",
      width: 70,
      renderCell: (params) => {
        const typeValue = getVehicleTypeValue(params.row?.type);

        return (
          <Box
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              handleTypeModalOpen(params.row);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.stopPropagation();
                event.preventDefault();
                handleTypeModalOpen(params.row);
              }
            }}
            sx={{
              alignItems: "center",
              color: colors.greenAccent[300],
              cursor: "pointer",
              display: "flex",
              font: "inherit",
              height: "100%",
              overflow: "hidden",
              textAlign: "left",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {typeValue}
          </Box>
        );
      },
    },
    {
      headerName: "Placa",
      field: "plate",
      width: 70,
    },
    {
      headerName: "Año",
      field: "year",
      width: 60,
    },
    {
      headerName: "Color",
      field: "color",
      width: 100,
    },
    {
      headerName: "Cilindrada",
      field: "cylinder",
      width: 70,
    },
    {
      headerName: "Peso",
      field: "weight",
      width: 60,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      renderCell: ({ row: { id, status } }) => {
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {
                navigate(`/admin/vehicle/${id}`);
              }}
            >
              <BorderColorOutlinedIcon />
            </IconButton>
            <IconButton
              color="success"
              aria-label="add"
              component="label"
              sx={{ background: "#ffff" }}
              onClick={() => {}}
            >
              <CheckCircleIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  GridCellExpand.propTypes = {
    value: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
  };

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  renderCellExpand.propTypes = {
    colDef: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
  };

  const [open, setOpen] = useState(false);
  const handleModalClose = () => setOpen(false);

  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="10px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header title="VEHICULOS - ACTULIZADOS" subtitle="" />
              <Box></Box>
            </Box>
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 2">
                <Box
                  m="10px 0 0 0"
                  height="100vh"
                  sx={{
                    "& .MuiDataGrid-root": {
                      border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "none",
                    },
                    "& .name-column--cell": {
                      color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                      color: `${colors.greenAccent[200]} !important`,
                    },
                  }}
                >
                  <DataGrid
                    getRowId={(row) => row.id}
                    rows={vehicles}
                    columns={columns}
                    localeText={
                      esES.components.MuiDataGrid.defaultProps.localeText
                    }
                    checkboxSelection={true}
                    onSelectionModelChange={(ids) => {
                      setDataSelected([]);
                      ids.map((id) => {
                        const select = vehicles.find((row) => row.id === id);
                        setDataSelected((oldData) => [...oldData, select]);
                      });
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Modal open={openTypeModal} onClose={handleTypeModalClose}>
            <Box backgroundColor={colors.primary[400]} sx={modalStyle}>
              <Typography variant="h6" component="h2" textAlign={"center"}>
                TIPO DE VEHICULO
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ my: 2 }}>
                    <InputLabel id="vehicle-type-select-label">Tipo</InputLabel>
                    <Select
                      labelId="vehicle-type-select-label"
                      value={selectedVehicleType}
                      label="Tipo"
                      onChange={(event) =>
                        setSelectedVehicleType(event.target.value)
                      }
                    >
                      <MenuItem value="0">none</MenuItem>
                      {vehicleTypes.map((vehicleType) => (
                        <MenuItem
                          key={vehicleType.id}
                          value={String(vehicleType.id)}
                        >
                          {vehicleType.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} textAlign="center">
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={updateVehicleType}
                    disabled={isLoading}
                  >
                    UPDATE
                  </Button>
                </Grid>
                <Grid item xs={6} textAlign="center">
                  <Button
                    color="error"
                    variant="contained"
                    onClick={handleTypeModalClose}
                    disabled={isLoading}
                  >
                    CERRAR
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Modal>
          <SnackBar
            stateSnk={stateSnk}
            vertical={vertical}
            horizontal={horizontal}
            duration={5000}
            handleClose={handleSnkClose}
            severity={severity}
            messageSnk={messageSnk}
          />
        </main>
      </div>
    </>
  );
};

export default VehicleRenovateList;
